import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../baseQuery';
import { appendCardFilterParams } from 'store/services/cards/search/cardsSearchApi';
import type { SearchRequestArgs } from 'screens/Main/Search/searchState';
import { SortOrder } from 'types/interfaces/sort';
import {
  CollectionListFetchResponse,
} from '../collectionList/types';
import { CollectionList } from 'types/interfaces/collectionList';

export interface PublicHaveListFetchResponse extends CollectionListFetchResponse {
  haveList: CollectionList[];
}

export interface PublicWantListFetchResponse extends CollectionListFetchResponse {
  wantList: CollectionList[];
}

export interface PublicCollectionFetchRequest {
  username: string;
  page?: number;
  limit?: number;
  sort?: string;
  direction?: SortOrder;
  search?: string;
  maxPrice?: number;
  autoMatch?: boolean;
  /** Card-attribute filters (Search vocabulary), serialized with appendCardFilterParams. */
  filters?: SearchRequestArgs;
}

export const publicCollectionApi = createApi({
  reducerPath: 'publicCollectionApi',
  baseQuery: baseQuery,
  tagTypes: ['PublicWantList', 'PublicHaveList'],
  endpoints: (builder) => ({
    fetchPublicHaveList: builder.query<
      PublicHaveListFetchResponse,
      PublicCollectionFetchRequest
    >({
      query: ({ username, page = 1, limit = 100, sort, direction, search, filters }) => {
        const params = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
        });

        if (sort) {
          params.append('sort', sort);
        }

        if (direction) {
          params.append('direction', direction);
        }

        if (search) {
          params.append('search', search);
        }

        if (filters) {
          appendCardFilterParams(params, filters);
        }

        return {
          url: `/public/collection/${username}/have-list?${params.toString()}`,
          method: 'GET',
          requiresAuth: false, // Public endpoint - no authentication required
        };
      },
      providesTags: (result, error, { username }) => [
        { type: 'PublicHaveList', id: username },
      ],
    }),
    fetchPublicWantList: builder.query<
      PublicWantListFetchResponse,
      PublicCollectionFetchRequest
    >({
      query: ({ username, page = 1, limit = 100, sort, direction, search, maxPrice, autoMatch, filters }) => {
        const params = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
        });

        if (sort) {
          params.append('sort', sort);
        }

        if (direction) {
          params.append('direction', direction);
        }

        if (search) {
          params.append('search', search);
        }

        if (maxPrice !== undefined) {
          params.append('maxPrice', maxPrice.toString());
        }

        if (autoMatch) {
          params.append('autoMatch', 'true');
        }

        if (filters) {
          appendCardFilterParams(params, filters);
        }

        return {
          url: `/public/collection/${username}/want-list?${params.toString()}`,
          method: 'GET',
          requiresAuth: !!autoMatch, // Need auth when auto-matching so backend can identify sender
        };
      },
      transformResponse: (response: PublicWantListFetchResponse) => ({
        ...response,
        wantList: response.wantList?.map((item: any) => ({
          ...item,
          acceptableLanguages: item.acceptedLanguages ?? item.acceptableLanguages,
        })),
      }),
      providesTags: (result, error, { username }) => [
        { type: 'PublicWantList', id: username },
      ],
    }),
  }),
});

export const {
  useFetchPublicHaveListQuery,
  useFetchPublicWantListQuery,
} = publicCollectionApi;
